import { Grid } from '@mui/material';
import { AddButton } from '../../../../components/simple/AllButtons';
import { FC, useState, useCallback } from 'react';
import StaffAttendanceTracker from './staffAttendanceTracker';
import DateBasedAttendanceComponent from '../../../../components/attendanceComponent/dateBasedAttendanceComponent';
import UserBasedAttendanceComponent from '../../../../components/attendanceComponent/userBasedAttendanceComponent';
import {
  useStaffAttendanceActions,
  useStaffAttendanceState,
  ATTENDANCE_VIEW_MODE,
} from '../../contexts/staffAttendance.context';
import { useStaffService } from '../../../../../apis/staff/useStaffService';
import { useTranslation } from 'src/hooks/useTranslationHook';
import ControlledDateRangePicker from '../../../../components/controlledComponents/datePicker/datePicker.component';
import { PermissionGate } from '../../../../../components/rbac/PermissionGate';
import { RbacDeniedRedirect } from '../../../../../components/rbac/RbacDeniedRedirect';
import { TAttendanceByUserResponse } from '../../../../../apis/__shared/attendance.interfaces';

interface StaffAttendanceProps {
  // view props goes here
}

const StaffAttendance: FC<StaffAttendanceProps> = () => {
  const {
    staffAttendanceRecords,
    staffAttendanceByUserRecords,
    staffAttendanceAppState: {
      isAttendanceTrackerOpen,
      dateRange,
      userParams,
      viewMode,
      selectedStaffId,
    },
  } = useStaffAttendanceState();
  const {
    setIsAttendanceTrackerOpen,
    setIsEditMode,
    setSelectedAttendance,
    setDateRange,
    setSelectedStaffId,
    setViewMode,
  } = useStaffAttendanceActions();

  const [isDetailsView, setIsDetailsView] = useState(false);
  const { translateText } = useTranslation();
  const { getStaffAttendanceByUser } = useStaffService();

  // Fetch function for user-based attendance
  const handleFetchUserAttendance = useCallback(
    async (userId: string): Promise<TAttendanceByUserResponse> => {
      setSelectedStaffId(userId);
      setViewMode(ATTENDANCE_VIEW_MODE.BY_USER);
      return getStaffAttendanceByUser(userParams, dateRange, userId);
    },
    [
      userParams,
      dateRange,
      setSelectedStaffId,
      setViewMode,
      getStaffAttendanceByUser,
    ]
  );

  const handleBackToDateView = useCallback(() => {
    setViewMode(ATTENDANCE_VIEW_MODE.BY_DATE);
    setSelectedStaffId(undefined);
  }, [setViewMode, setSelectedStaffId]);

  const handleDateRangeChange = (fromDate: any, toDate: any) => {
    setDateRange(fromDate, toDate);
  };

  const onTrackAttendanceClickHandler = () => {
    setIsAttendanceTrackerOpen(true);
  };

  const onEditAttendanceClickHandler = (data: any) => {
    setIsAttendanceTrackerOpen(true);
    setIsEditMode(true);
    setSelectedAttendance(data);
  };

  return (
    <PermissionGate
      module="staff"
      submodule="attendance"
      permission="read"
      fallback={<RbacDeniedRedirect />}
    >
      <Grid 
        container 
        spacing={{ xs: 1, sm: 2 }}
        sx={{
          p: { xs: 1, sm: 2 },
        }}
        id="staffAttendanceContainer"
        data-testid="staff-attendance-container"
      >
        {!isDetailsView &&
          viewMode === ATTENDANCE_VIEW_MODE.BY_DATE && (
            <Grid
              item
              xs={12}
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                justifyContent: 'space-between',
                gap: { xs: 1, sm: 2 },
                mb: { xs: 1, sm: 0 },
              }}
              data-testid="staff-attendance-date-range-container"
            >
            <ControlledDateRangePicker
              id="staff-attendance-date-range"
              name="dateRange"
              placeholder={`${translateText('staff.attendance.fromDate')} - ${translateText('staff.attendance.toDate')}`}
              value={[dateRange.fromDate, dateRange.toDate]}
              onChange={handleDateRangeChange}
              isGridItem={false}
              isClearable
              dataTestId="staff-attendance-date-range-picker"
            />
            <PermissionGate
              module="staff"
              submodule="attendance"
              permission="write"
            >
              <AddButton
                label={translateText('staff.attendance.trackAttendance')}
                onClickHandler={onTrackAttendanceClickHandler}
                data-testid="staff-attendance-add-button"
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                }}
              />
            </PermissionGate>
          </Grid>
        )}
        <Grid item xs={12}>
          {viewMode === ATTENDANCE_VIEW_MODE.BY_USER &&
          selectedStaffId &&
          staffAttendanceByUserRecords ? (
            <UserBasedAttendanceComponent
              atendanceRecords={staffAttendanceByUserRecords}
              onEditAttendanceClickHandler={onEditAttendanceClickHandler}
              onFetchUserAttendance={handleFetchUserAttendance}
              userId={selectedStaffId}
              onBackToDateView={handleBackToDateView}
              hideBackButton={false}
            />
          ) : (
            <DateBasedAttendanceComponent
              atendanceRecords={staffAttendanceRecords}
              onEditAttendanceClickHandler={onEditAttendanceClickHandler}
              onViewChange={setIsDetailsView}
              onFetchUserAttendance={handleFetchUserAttendance}
            />
          )}
        </Grid>
        <StaffAttendanceTracker
          isOpen={isAttendanceTrackerOpen}
          onCloseAttendanceTrackerModal={() => {
            setIsAttendanceTrackerOpen(false);
          }}
        />
      </Grid>
    </PermissionGate>
  );
};

export default StaffAttendance;
